import { FC } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { HELP_ROUTE } from 'components/help/constants';
import { UrlParams } from 'common/types';
import { Cell, Column, PlotData } from 'common/graphqlTypes';
import { addCommas } from 'common/util/number';
import { useSetSearchParams } from 'hooks/useSetSearchParams';
import { DATA_SOURCE_NAME_FIELD } from 'schemas/constants';

import { searchParamState } from 'components/common/Managers/RouteStateManager.state';
import { dataDetailsCardTriggerState } from 'components/common/Managers/ViewStateManager.state';
import Link from 'components/common/Link/Link';
import PowerQueryChartResults from 'components/search/PowerQueriesPage/components/PowerQueryCharts/PowerQueryChartResults';
import InspectFieldsDrawer from 'components/search/SearchLogsPage/InspectFieldsDrawer';

import ErrorBody from '../ErrorBody/ErrorBody';
import NoEventsFound from '../NoEventsFound/NoEventsFound';
import GettingStartedWithPQ from '../GettingStartedWithPQ/GettingStartedWithPQ';
import Table from '../PowerQueryTable/PowerQueryTable';
import { Values } from '../../types';

type PowerQueriesContentProps = ResponseBodyProps & {
  error: boolean;
  errorMessages: Record<string, string>;
  isDrawerOpen: boolean;
  closeDrawer: () => void;
  omitIntro?: boolean;
};

type ResponseBodyProps = {
  sendQuery: string;
  columns: Column[];
  values: Values;
  cells: Cell[][];
  matchCount: number;
  omittedEvents: number;
  partialResultsDueToTimeLimit: boolean;
  plotResult?: PlotData;
  isLimitAdded?: boolean;
};

const getPercentsFromRatio = (dividend: number, divider: number): string => ((dividend / divider) * 100).toFixed(1);

type IncompleteResultsMessagesProps = {
  matchCount: number;
  omittedEvents: number;
  partialResultsDueToTimeLimit: boolean;
  isLimitAdded?: boolean;
  rowCount?: number;
};

export const IncompleteResultsMessages: FC<IncompleteResultsMessagesProps> = ({
  matchCount,
  omittedEvents,
  partialResultsDueToTimeLimit,
  isLimitAdded,
  rowCount = 0,
}: IncompleteResultsMessagesProps) => {
  const setSearchParams = useSetSearchParams();
  const filter = useRecoilValue(searchParamState(K.URL_PARAM.FILTER));

  const showAll = () => {
    // would be better to use actual knob value groupingTableMaximumRows instead of max 100000, when it's available
    setSearchParams({ [K.URL_PARAM.FILTER]: `${filter}|limit 100000` } as UrlParams, true);
  };

  let resultsEl = <div>{rowCount} rows returned</div>;
  if (isLimitAdded && rowCount >= K.PQ_DEFAULT_LIMIT) {
    resultsEl = (
      <p>
        First {K.PQ_DEFAULT_LIMIT} rows shown.
        <span className="link PowerQueries__ShowAll" onClick={() => showAll()}>
          Show all
        </span>
      </p>
    );
  }

  return (
    <div className="PowerQueries__IncompleteResultsMessages">
      {omittedEvents > 0 && (
        <p>
          {`${addCommas(omittedEvents)} of ${addCommas(matchCount)} events (${getPercentsFromRatio(
            omittedEvents,
            matchCount,
          )}%) were omitted due to memory limits. See `}
          <Link className="link" href={HELP_ROUTE.HELP_POWER_QUERIES_MEMORY_LIMIT} openInNewTab>
            query memory limits
          </Link>
          {' for more information.'}
        </p>
      )}
      {partialResultsDueToTimeLimit && <p>The search partially timed out, so results may be incomplete.</p>}
      {resultsEl}
    </div>
  );
};

const ResponseBody: FC<ResponseBodyProps> = ({
  columns,
  values,
  cells,
  sendQuery,
  matchCount,
  plotResult,
  omittedEvents,
  partialResultsDueToTimeLimit,
  isLimitAdded,
}: ResponseBodyProps) => {
  const graphStyleParam = useRecoilValue(searchParamState(K.URL_PARAM.GRAPH_STYLE));

  if (matchCount === 0 && sendQuery !== null) {
    return <NoEventsFound />;
  }

  let content = (
    <>
      <IncompleteResultsMessages
        matchCount={matchCount}
        omittedEvents={omittedEvents}
        partialResultsDueToTimeLimit={partialResultsDueToTimeLimit}
        isLimitAdded={isLimitAdded}
        rowCount={cells.length}
      />
      <Table headers={columns} rowValues={values} />
    </>
  );
  // PQ data visualizations.
  if (graphStyleParam) {
    content = <PowerQueryChartResults columns={columns} cells={cells} plotResult={plotResult} />;
  }

  return (
    <div className="PowerQueries__TableContainer" data-test-id={ELEMENT.powerQueryTableContainer}>
      {content}
    </div>
  );
};

const PowerQueriesContent: FC<PowerQueriesContentProps> = ({
  error,
  errorMessages,
  sendQuery,
  columns,
  values,
  cells,
  matchCount,
  plotResult,
  omittedEvents,
  partialResultsDueToTimeLimit = false,
  omitIntro = false,
  isLimitAdded = false,
  isDrawerOpen,
  closeDrawer,
}: PowerQueriesContentProps) => {
  const [dataDetailsFieldTrigger] = useRecoilState(dataDetailsCardTriggerState);

  if (!sendQuery || sendQuery === '') {
    return omitIntro ? <></> : <GettingStartedWithPQ />;
  }

  if (error) {
    return <ErrorBody errorMessages={errorMessages} sendQuery={sendQuery} />;
  }

  const dataSource = dataDetailsFieldTrigger?.data?.[DATA_SOURCE_NAME_FIELD] as string;

  return (
    <>
      <ResponseBody
        columns={columns}
        values={values}
        cells={cells}
        sendQuery={sendQuery}
        matchCount={matchCount}
        plotResult={plotResult}
        omittedEvents={omittedEvents}
        partialResultsDueToTimeLimit={partialResultsDueToTimeLimit}
        isLimitAdded={isLimitAdded}
      />
      {dataDetailsFieldTrigger && (
        <InspectFieldsDrawer open={isDrawerOpen} close={closeDrawer} dataSource={dataSource} />
      )}
    </>
  );
};

export default PowerQueriesContent;
